import { useEffect, useState } from "react";
import Skeleton from "Components/Common/Skeleton/Skeleton";
import {
  getLenderDraws,
  getLenderDrawDetail,
} from "../../../services/lenderService";
import { STATUS_CLASS, STATUS_LABEL } from "utils/Common/Constant";
import DrawActionModal from "./DrawActionModal";
import EmptyState from "Components/Common/EmptyState/EmptyState";
import EmptyDrawHistoryIcon from "Assets/Img/EmptyDrawHistoryIcon";
import { downloadDocumentBlob } from "../../../services/projectService";
import { triggerBlobDownload } from "utils/Common/fileDownload";
import DownloadIcon from "Assets/Img/DownloadIcon";
import Table, { Column } from "Components/Table/Table";
import Button from "Components/Common/Button/Button";

type ModalType = "approve" | "reject" | "comment";

// ── List API types ─────────────────────────────────────────────────────────

interface DrawAmount {
  amount: number;
  currency: { symbol: string };
}

interface ApiDraw {
  id: string;
  drawNumber: number;
  toDate: string | null;
  status: string;
  requestedAmount: DrawAmount;
  approvedAmount: DrawAmount | null;
  inspection: { id: string; inspectionDate: string } | null;
  lienWaivers: { total: number; signed: number };
}
// ── Detail API types ───────────────────────────────────────────────────────
interface LienWaiverDetail {
  id: string;
  companyName: string;
  totalAmount: DrawAmount;
  paidAmount: DrawAmount | null;
  remainingAmount: DrawAmount | null;
  signedAt: string | null;
}

interface InspectionItem {
  id: string;
  itemLabel: string;
  isFlagged: boolean;
  isIncludedFlagInReport: boolean;
  note: Record<string, string>;
}

interface DrawDetail {
  drawRequest: {
    id: string;
    drawNumber: number;
    status: string;
    requestedAmount: DrawAmount;
    approvedAmount: DrawAmount | null;
    isLenderApproved: boolean | null;
    lenderActionDate: string | null;
    lenderNote: string | null;
    lienWaivers: LienWaiverDetail[];
  };
  report: {
    id?: string;
    reportNumber?: number;
    inspectionSnapshot: {
      phases: Array<{ items: InspectionItem[] }>;
    };
  } | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const fmt = (m: DrawAmount): string =>
  `${m.currency.symbol}${m.amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
};

const formatPeriod = (iso: string | null): string => {
  if (!iso) return "—";
  return `Through ${formatDate(iso)}`;
};

const buildDrawSummary = (
  draw: ApiDraw,
  detail: DrawDetail | undefined,
): string => {
  const parts: string[] = [];

  // Waiver status — available from list API immediately
  if (draw.lienWaivers.total === 0) {
    parts.push("No lien waivers required.");
  } else if (draw.lienWaivers.signed === draw.lienWaivers.total) {
    parts.push("All waivers received.");
  } else {
    parts.push(
      `${draw.lienWaivers.signed} of ${draw.lienWaivers.total} waivers signed.`,
    );
  }

  // Flag + approval status — only once detail is loaded
  if (detail) {
    const flagCount =
      detail.report?.inspectionSnapshot?.phases
        ?.flatMap((p) => p.items)
        .filter((i) => i.isFlagged && i.isIncludedFlagInReport).length ?? 0;

    parts.push(
      flagCount === 0
        ? "No flags."
        : `${flagCount} flag${flagCount > 1 ? "s" : ""} raised.`,
    );

    if (
      detail.drawRequest.isLenderApproved === true &&
      detail.drawRequest.lenderActionDate
    ) {
      parts.push(
        `Approved ${formatDate(detail.drawRequest.lenderActionDate)}.`,
      );
    } else if (detail.drawRequest.isLenderApproved === false) {
      parts.push("Rejected by lender.");
    }
  }

  return parts.join(" ");
};

// ── Component ──────────────────────────────────────────────────────────────

interface DrawHistoryProps {
  projectId?: string;
  expandedDraw: string | null;
  setExpandedDraw: (d: string | null) => void;
  setOpenModal: (m: ModalType | null) => void;
  showToast: (msg: string) => void;
}

export default function DrawHistory({
  projectId,
  expandedDraw,
  setExpandedDraw,
  showToast,
}: DrawHistoryProps) {
  const [draws, setDraws] = useState<ApiDraw[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailCache, setDetailCache] = useState<Record<string, DrawDetail>>(
    {},
  );
  const [detailLoading, setDetailLoading] = useState<Record<string, boolean>>(
    {},
  );
  const [downloadingReport, setDownloadingReport] = useState<
    Record<string, boolean>
  >({});

  const handleDownloadReport = async (drawId: string) => {
    const detail = detailCache[drawId];
    const reportId = detail?.report?.id;
    if (!projectId || !reportId) return;
    setDownloadingReport((prev) => ({ ...prev, [drawId]: true }));
    try {
      const blob = await downloadDocumentBlob(projectId, reportId, true);
      if (blob) {
        const reportNum = detail?.report?.reportNumber ?? "report";
        triggerBlobDownload(blob, `ProgressReport-${reportNum}.pdf`);
      } else {
        showToast("Failed to download report. Please try again.");
      }
    } catch {
      showToast("Error downloading report. Please try again.");
    } finally {
      setDownloadingReport((prev) => ({ ...prev, [drawId]: false }));
    }
  };

  const [pendingAction, setPendingAction] = useState<{
    action: "approved" | "rejected";
    drawId: string;
    drawNumber: number;
  } | null>(null);

  const fetchDrawDetail = async (drawId: string) => {
    if (!projectId || detailCache[drawId] || detailLoading[drawId]) return;
    setDetailLoading((prev) => ({ ...prev, [drawId]: true }));
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (getLenderDrawDetail(
        projectId,
        drawId,
      ) as Promise<{ data: any; error: any }>);
      if (error) {
        showToast("Failed to load draw details.");
        return;
      }
      if (data?.data) {
        setDetailCache((prev) => ({
          ...prev,
          [drawId]: data.data as DrawDetail,
        }));
      }
    } catch {
      showToast("Failed to load draw details.");
    } finally {
      setDetailLoading((prev) => ({ ...prev, [drawId]: false }));
    }
  };

  const loadDraws = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (getLenderDraws(projectId) as Promise<{
        data: any;
        error: any;
      }>);
      if (error) {
        showToast("Failed to load draw history.");
        return;
      }
      const raw: ApiDraw[] = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
          ? data
          : [];
      const sorted = [...raw].sort((a, b) => a.drawNumber - b.drawNumber);
      setDraws(sorted);
      // Auto-expand the latest draw (last in ascending order)
      if (sorted.length > 0) {
        const latest = sorted[sorted.length - 1];
        setExpandedDraw(latest.id);
        fetchDrawDetail(latest.id);
      }
    } catch {
      showToast("Failed to load draw history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setExpandedDraw(null);
    setDetailCache({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadDraws();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRowClick = async (drawId: string) => {
    const willOpen = expandedDraw !== drawId;
    setExpandedDraw(willOpen ? drawId : null);
    if (willOpen) {
      await fetchDrawDetail(drawId);
    }
  };

  if (loading) {
    return (
      <div className="draw-list">
        {[0, 1, 2].map((i) => (
          <Skeleton
            key={i}
            variant="card"
            width="100%"
            height={44}
            style={{ marginBottom: 6 }}
          />
        ))}
      </div>
    );
  }

  if (!draws.length) {
    return (
      <EmptyState
        icon={<EmptyDrawHistoryIcon />}
        title="No Draws on Record"
        subtitle="Draw requests submitted for this project will appear here once the first disbursement is initiated."
      />
    );
  }

  return (
    <>
      <div className="draw-history-scroll">
        <div className="draw-hdr draw-row">
          <span className="dh">Draw #</span>
          <span className="dh">Period</span>
          <span className="dh">Inspection</span>
          <span className="dh">Requested</span>
          <span className="dh">Waivers</span>
          <span className="dh">Status</span>
        </div>
        <div className="draw-list">
          {draws.map((draw) => {
            const isOpen = expandedDraw === draw.id;
            const waivers =
              draw.lienWaivers.total > 0
                ? `${draw.lienWaivers.signed} of ${draw.lienWaivers.total} signed`
                : "None";
            const statusKey = draw.status.toLowerCase();
            const detail = detailCache[draw.id];
            const loadingDetail = detailLoading[draw.id];

            const flaggedItems =
              detail?.report?.inspectionSnapshot?.phases
                ?.flatMap((p) => p.items)
                .filter((i) => i.isFlagged && i.isIncludedFlagInReport) ?? [];

            return (
              <div key={draw.id}>
                <div
                  className="draw-row"
                  onClick={() => handleRowClick(draw.id)}
                >
                  <span className="dv-bold">
                    #{String(draw.drawNumber).padStart(2, "0")}
                  </span>
                  <span className="dv">{formatPeriod(draw.toDate)}</span>
                  <span className="dv">
                    {draw.inspection
                      ? formatDate(draw.inspection.inspectionDate)
                      : "—"}
                  </span>
                  <span className="dv-bold">{fmt(draw.requestedAmount)}</span>
                  <span
                    className={`dv ${draw.lienWaivers.signed === draw.lienWaivers.total && draw.lienWaivers.total > 0 ? "dvGreen" : ""}`}
                  >
                    {waivers}
                  </span>
                  <span
                    className={`badge ${STATUS_CLASS[statusKey] ?? "b-pre"} badgeSm`}
                  >
                    {STATUS_LABEL[statusKey] ?? draw.status}
                  </span>
                </div>
                <div className={`draw-detail${isOpen ? " open" : ""}`}>
                  {isOpen && (
                    <>
                      {loadingDetail && (
                        <div className="drawDetailLoading">
                          {[0, 1, 2].map((i) => (
                            <Skeleton
                              key={i}
                              variant="card"
                              width="100%"
                              height={32}
                              style={{ marginBottom: 6 }}
                            />
                          ))}
                        </div>
                      )}

                      {!loadingDetail && (
                        <div className="drawDetailMeta">
                          {buildDrawSummary(draw, detail)}
                        </div>
                      )}

                      {!loadingDetail && detail && (
                        <>
                          {flaggedItems.length > 0 && (
                            <div className="drawFlagBox">
                              <div className="drawFlagHdr">
                                ⚠ {flaggedItems.length} CSC FLAG
                                {flaggedItems.length > 1 ? "S" : ""}
                              </div>
                              <div className="drawFlagList">
                                {flaggedItems.map((item) => (
                                  <span key={item.id} className="drawFlagItem">
                                    {item.itemLabel}
                                    {item.note.flag
                                      ? ` (${item.note.flag})`
                                      : ""}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          <Table
                            tableClassName="lien-tbl"
                            data={detail.drawRequest.lienWaivers}
                            columns={
                              [
                                {
                                  header: "Vendor",
                                  render: (lw) => lw.companyName,
                                },
                                {
                                  header: "Earned",
                                  render: (lw) => fmt(lw.totalAmount),
                                },
                                {
                                  header: "Less Ret.",
                                  render: (lw) =>
                                    lw.paidAmount ? fmt(lw.paidAmount) : "—",
                                },
                                {
                                  header: "Received",
                                  render: (lw) =>
                                    lw.paidAmount ? fmt(lw.paidAmount) : "—",
                                },
                                {
                                  header: "Delta",
                                  render: (lw) => (
                                    <span
                                      className={
                                        lw.remainingAmount?.amount === 0
                                          ? "dok"
                                          : ""
                                      }
                                    >
                                      {lw.remainingAmount
                                        ? fmt(lw.remainingAmount)
                                        : "—"}
                                    </span>
                                  ),
                                },
                              ] as Column<LienWaiverDetail>[]
                            }
                          />

                          {statusKey === "lender_action" && (
                            <div className="drawDetailActions">
                              <Button
                                className="btn btnPrimary approve-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPendingAction({
                                    action: "approved",
                                    drawId: detail.drawRequest.id,
                                    drawNumber: detail.drawRequest.drawNumber,
                                  });
                                }}
                              >
                                Approve Draw
                              </Button>
                              <Button
                                className="btn btn-danger reject-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPendingAction({
                                    action: "rejected",
                                    drawId: detail.drawRequest.id,
                                    drawNumber: detail.drawRequest.drawNumber,
                                  });
                                }}
                              >
                                Reject Draw
                              </Button>
                              <Button
                                className="btn"
                                disabled={
                                  downloadingReport[draw.id] ||
                                  !detail?.report?.id
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadReport(draw.id);
                                }}
                              >
                                {downloadingReport[draw.id] ? (
                                  <span className="btn-spinner" />
                                ) : (
                                  <DownloadIcon />
                                )}
                                Download Report
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <DrawActionModal
        projectId={projectId ?? ""}
        drawId={pendingAction?.drawId ?? ""}
        drawNumber={pendingAction?.drawNumber ?? 0}
        action={pendingAction?.action ?? null}
        onClose={() => setPendingAction(null)}
        onSuccess={() => {
          const drawId = pendingAction?.drawId;
          setPendingAction(null);
          if (drawId) {
            setDetailCache((prev) => {
              const next = { ...prev };
              delete next[drawId];
              return next;
            });
          }
          setExpandedDraw(null);
          loadDraws();
        }}
      />
    </>
  );
}
